import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  MessageCircle,
  Nfc,
  PackageCheck,
  QrCode,
  ShieldCheck,
  Smartphone,
  Star,
  Truck,
} from "lucide-react";

import { CatalogoCard } from "@/components/site/CatalogoCard";
import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { track } from "@/lib/analytics";
import { WHATSAPP } from "@/lib/contacto";
import { productos } from "@/lib/productos";

const canonical = "https://pymevisible.cl/stand-nfc-qr";
const modelosStand = productos.filter((producto) => producto.category === "stands");
const mensajeWhatsApp = `${WHATSAPP.split("?")[0]}?text=${encodeURIComponent(
  "Hola, vi los stands NFC + QR en la página de Pyme Visible y quiero cotizar uno para mi negocio.",
)}`;

const preguntas = [
  {
    pregunta: "¿Qué es un stand NFC y para qué sirve?",
    respuesta:
      "Es un letrero para el mesón que abre un enlace cuando el cliente acerca su teléfono. Puede dirigir a Google para solicitar reseñas, a WhatsApp, Instagram, TikTok u otra página del negocio.",
  },
  {
    pregunta: "¿El stand NFC también incluye código QR?",
    respuesta:
      "Sí. Nuestros stands incluyen NFC y QR para que el cliente pueda acercar el teléfono o escanear el código con la cámara.",
  },
  {
    pregunta: "¿El producto llega configurado?",
    respuesta:
      "Sí. Configuramos el chip NFC y el código QR con el enlace acordado, probamos ambos accesos y entregamos el producto listo para usar.",
  },
  {
    pregunta: "¿Hay que pagar una mensualidad?",
    respuesta:
      "No. El stand no requiere aplicaciones ni mensualidades. El precio corresponde al producto configurado según las condiciones acordadas al cotizar.",
  },
  {
    pregunta: "¿Puedo pedir un stand NFC personalizado?",
    respuesta:
      "Sí. Tenemos un modelo personalizado que puede incorporar el logo, los colores y el enlace que necesite tu negocio.",
  },
  {
    pregunta: "¿Realizan entregas en Santiago y envíos a regiones?",
    respuesta:
      "Coordinamos entrega gratuita a convenir dentro de nuestra zona urbana central de Santiago. Para sectores alejados coordinamos un punto de encuentro y para regiones informamos el medio, costo y plazo antes de confirmar.",
  },
];

export const Route = createFileRoute("/stand-nfc-qr")({
  component: StandNfcPage,
  head: () => ({
    meta: [
      { title: "Stand NFC + QR para negocios en Chile | Pyme Visible" },
      {
        name: "description",
        content:
          "Compra tu stand NFC + QR para Google, WhatsApp, Instagram o TikTok. Configurado, probado y listo para usar en negocios de Chile desde $20.000.",
      },
      { property: "og:title", content: "Stand NFC + QR para negocios | Pyme Visible" },
      {
        property: "og:description",
        content:
          "Stands NFC para conseguir más reseñas, seguidores o contactos. Revisa modelos, precios y cotiza por WhatsApp.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      {
        property: "og:image",
        content: "https://pymevisible.cl/productos/stand-google-negro-catalogo.webp",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Stand NFC + QR para negocios | Pyme Visible" },
      {
        name: "twitter:description",
        content: "Modelos para Google, WhatsApp, Instagram y TikTok desde $20.000.",
      },
      {
        name: "twitter:image",
        content: "https://pymevisible.cl/productos/stand-google-negro-catalogo.webp",
      },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${canonical}#webpage`,
              url: canonical,
              name: "Stand NFC + QR para negocios en Chile",
              description:
                "Modelos de stand NFC + QR configurados para Google, WhatsApp, Instagram y TikTok.",
              isPartOf: { "@id": "https://pymevisible.cl/#website" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Inicio",
                  item: "https://pymevisible.cl/",
                },
                { "@type": "ListItem", position: 2, name: "Stand NFC", item: canonical },
              ],
            },
            {
              "@type": "ItemList",
              name: "Modelos de stand NFC + QR",
              itemListElement: modelosStand.map((producto, index) => ({
                "@type": "ListItem",
                position: index + 1,
                url: `https://pymevisible.cl/productos/${producto.id}`,
                name: producto.name,
              })),
            },
            {
              "@type": "FAQPage",
              mainEntity: preguntas.map(({ pregunta, respuesta }) => ({
                "@type": "Question",
                name: pregunta,
                acceptedAnswer: { "@type": "Answer", text: respuesta },
              })),
            },
          ],
        }),
      },
    ],
  }),
});

function StandNfcPage() {
  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <section className="pv-page-hero overflow-hidden border-b border-ink/10">
          <div className="mx-auto max-w-[1320px] px-5 py-7 sm:px-8 sm:py-12 lg:px-10">
            <nav aria-label="Ruta de navegación" className="text-sm text-slate">
              <a href="/" className="hover:text-ink hover:underline">
                Inicio
              </a>
              <span aria-hidden="true"> / </span>
              <span>Stand NFC</span>
            </nav>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-primary sm:mt-7">
              NFC + QR configurado en Chile
            </p>
            <h1 className="mt-2 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.92] text-ink sm:mt-3 sm:text-6xl lg:text-7xl">
              Stand NFC + QR para negocios
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate sm:mt-5 sm:text-lg">
              Convierte el mesón de tu negocio en un acceso directo a tus reseñas de Google,
              WhatsApp o redes sociales. Recibes tu stand NFC configurado, probado y listo para que
              tus clientes lo usen con un toque o escaneando el QR.
            </p>
          </div>
        </section>

        <section
          id="modelos"
          aria-label="Modelos disponibles"
          className="pv-catalog-section scroll-mt-28"
        >
          <div className="mx-auto max-w-[1320px] px-5 pb-8 pt-5 sm:px-8 sm:pb-12 sm:pt-8 lg:px-10">
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-5">
              {modelosStand.map((producto) => (
                <CatalogoCard key={producto.id} producto={producto} headingLevel={2} />
              ))}
            </div>
          </div>
        </section>

        <BeneficiosStand />

        <section className="pv-dark-section text-white">
          <div className="mx-auto grid max-w-[1320px] gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-signal">
                Cómo funciona un stand NFC
              </p>
              <h2 className="mt-3 font-display text-4xl font-extrabold uppercase leading-none sm:text-5xl">
                Toca, abre y conecta.
              </h2>
              <p className="mt-5 max-w-lg leading-relaxed text-white/70">
                No necesitas instalar una aplicación. El stand utiliza un chip NFC programado y un
                QR visible como alternativa para abrir el mismo destino.
              </p>
            </div>
            <ol className="grid gap-4 sm:grid-cols-3">
              {[
                {
                  Icon: Nfc,
                  paso: "01",
                  title: "Acerca",
                  text: "El cliente acerca el teléfono al stand.",
                },
                {
                  Icon: QrCode,
                  paso: "02",
                  title: "Escanea",
                  text: "También puede usar la cámara y el QR.",
                },
                {
                  Icon: ArrowRight,
                  paso: "03",
                  title: "Abre",
                  text: "Llega a Google, WhatsApp o tu red social.",
                },
              ].map(({ Icon, paso, title, text }) => (
                <li key={paso} className="pv-dark-card rounded-3xl p-5">
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-signal" aria-hidden="true" />
                    <span className="font-mono text-xs text-white/50">{paso}</span>
                  </div>
                  <h3 className="mt-8 text-xl font-bold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="pv-services-section">
          <div className="mx-auto grid max-w-[1320px] gap-10 px-5 py-14 sm:px-8 lg:grid-cols-2 lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                Para negocios reales
              </p>
              <h2 className="mt-3 font-display text-4xl font-extrabold uppercase leading-none text-ink sm:text-5xl">
                Un stand para cada objetivo.
              </h2>
              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                {[
                  ["Google", "Facilita que clientes reales lleguen al formulario de reseñas."],
                  ["WhatsApp", "Abre una conversación directa con el número de tu negocio."],
                  ["Instagram", "Lleva al perfil para mostrar contenido y conseguir seguidores."],
                  ["TikTok", "Conecta el mesón con el perfil y los videos de tu marca."],
                ].map(([title, text]) => (
                  <article key={title} className="pv-surface-card rounded-2xl p-5">
                    <h3 className="font-bold text-ink">Stand NFC para {title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate">{text}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="pv-inline-cta rounded-[2rem] p-7 sm:p-9">
              <ShieldCheck className="h-8 w-8 text-primary" aria-hidden="true" />
              <h2 className="mt-5 text-3xl font-extrabold uppercase text-ink">
                Lo recibes listo para usar
              </h2>
              <ul className="mt-6 grid gap-4 text-slate">
                {[
                  "Confirmamos contigo el enlace correcto antes de programarlo.",
                  "Probamos la lectura NFC y el funcionamiento del código QR.",
                  "Te mostramos el alcance y el precio antes de confirmar el pedido.",
                  "Coordinamos entrega en Santiago o envío a regiones.",
                ].map((texto) => (
                  <li key={texto} className="flex gap-3">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                    <span>{texto}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="pv-faq-section" aria-labelledby="preguntas-stand-nfc">
          <div className="mx-auto grid max-w-[1100px] gap-8 px-5 py-14 sm:px-8 lg:grid-cols-[.75fr_1.25fr] lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                Preguntas
              </p>
              <h2
                id="preguntas-stand-nfc"
                className="mt-3 font-display text-4xl font-extrabold uppercase leading-none text-ink sm:text-5xl"
              >
                Antes de elegir tu stand.
              </h2>
            </div>
            <div className="divide-y divide-ink/10 border-y border-ink/10">
              {preguntas.map(({ pregunta, respuesta }) => (
                <details key={pregunta} className="pv-accordion-row group py-1">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 py-5 font-bold text-ink [&::-webkit-details-marker]:hidden">
                    {pregunta}
                    <span
                      className="text-xl text-primary transition-transform group-open:rotate-45"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>
                  <p className="max-w-2xl pb-5 text-sm leading-relaxed text-slate">{respuesta}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="pv-final-cta">
          <div className="mx-auto flex max-w-[1100px] flex-col items-center px-5 py-12 text-center sm:px-8">
            <Truck className="h-8 w-8 text-primary" aria-hidden="true" />
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-none text-ink sm:text-5xl">
              Cotiza tu stand NFC
            </h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-slate">
              Cuéntanos qué enlace quieres conectar y cuántas unidades necesitas. Te confirmamos
              modelo, precio, configuración y forma de entrega antes de preparar el pedido.
            </p>
            <a
              href={mensajeWhatsApp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { ubicacion: "landing_stand_nfc_final" })}
              className="pv-button pv-button--primary mt-7 inline-flex min-h-12 items-center justify-center gap-2 px-7 py-3.5"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Comprar por WhatsApp
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function BeneficiosStand({ className }: { className?: string }) {
  const beneficios = [
    {
      Icon: Star,
      title: "Más fácil pedir una reseña",
      text: "El cliente llega al enlace de Google sin tener que buscar tu negocio.",
    },
    {
      Icon: Smartphone,
      title: "Dos formas de conectar",
      text: "Puede acercar un teléfono compatible o escanear el código QR.",
    },
    {
      Icon: PackageCheck,
      title: "Configurado y comprobado",
      text: "Programamos el enlace y revisamos su funcionamiento antes de entregar.",
    },
  ];

  return (
    <section className={`pv-benefit-band ${className ?? ""}`}>
      <div className="pv-benefit-rail mx-auto grid max-w-[1240px] grid-cols-3 gap-0 px-3 sm:px-8 lg:px-10">
        {beneficios.map(({ Icon, title, text }) => (
          <article
            key={title}
            className="border-r border-ink/10 px-2 py-4 text-center last:border-r-0 sm:px-5 sm:py-7 sm:text-left"
          >
            <Icon
              className="mx-auto h-5 w-5 text-primary sm:mx-0 sm:h-6 sm:w-6"
              aria-hidden="true"
            />
            <h2 className="mt-2 text-[0.7rem] font-bold leading-tight text-ink sm:mt-3 sm:text-lg">
              {title}
            </h2>
            <p className="mt-1 hidden text-sm leading-relaxed text-slate sm:block">{text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
